import { BarDatum } from '@nivo/bar';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EnumListKey, useAllEnums } from '~/data/enums';
import { PersonnelReportData } from '~/data/reports';

import { analyticsColors } from '../../common/colors';

type PersonnelReportTableDataRow = {
  [key: string]: { count: number; tableData: PersonnelReportData[] };
};

enum AgeRanges {
  Age24OrUnder = '<=24',
  Age25To34 = '25-34',
  Age35To44 = '35-44',
  Age45To54 = '45-54',
  Age55To64 = '55-64',
  Age65OrOver = '>=65',
}

export const usePersonnelReport = (data: PersonnelReportData[]) => {
  const { t } = useTranslation();
  const { getEnums } = useAllEnums();

  const jsonCountries = getEnums(EnumListKey.COUNTRY);
  const jsonCountriesIndexedByCode = useMemo(() => {
    return jsonCountries.reduce((acc, countryEnum) => {
      acc[countryEnum.code] = countryEnum.name;
      return acc;
    }, {} as { [key: string]: string });
  }, [jsonCountries]);
  const jsonCountryData = useMemo(() => {
    return data?.reduce((acc, personnel) => {
      const country = personnel.fEmpWorkCountry;
      const key = jsonCountriesIndexedByCode[country] || t('laNoCountry');

      if (!acc[key]) {
        acc[key] = {
          count: 0,
          tableData: [],
        };
      }

      acc[key].count++;
      acc[key].tableData.push(personnel);

      return acc;
    }, {} as { [key: string]: { count: number; tableData: PersonnelReportData[] } });
  }, [data, jsonCountriesIndexedByCode]);
  const numberOfEmployeesReportData = Object.entries(jsonCountryData).map(
    ([key, value]): BarDatum => ({
      country: key,
      [t('laNumberOfEmp')]: value.count,
    }),
  );

  const ageReportTableData = useMemo(() => {
    const getAgeKey = (age: number): AgeRanges => {
      if (age <= 24) {
        return AgeRanges.Age24OrUnder;
      } else if (age <= 34) {
        return AgeRanges.Age25To34;
      } else if (age <= 44) {
        return AgeRanges.Age35To44;
      } else if (age <= 54) {
        return AgeRanges.Age45To54;
      } else if (age <= 64) {
        return AgeRanges.Age55To64;
      } else {
        return AgeRanges.Age65OrOver;
      }
    };

    const noRangeData = {
      count: 0,
      tableData: [],
    };

    const initialRanges = {
      [AgeRanges.Age24OrUnder]: noRangeData,
      [AgeRanges.Age25To34]: noRangeData,
      [AgeRanges.Age35To44]: noRangeData,
      [AgeRanges.Age45To54]: noRangeData,
      [AgeRanges.Age55To64]: noRangeData,
      [AgeRanges.Age65OrOver]: noRangeData,
    };

    const results = data.reduce((acc, personnel) => {
      const age = moment().diff(moment(personnel.fEmpDOB), 'years');
      if (isNaN(age)) return acc;

      const key = getAgeKey(age);

      if (!acc[key]) {
        acc[key] = {
          count: 0,
          tableData: [],
        };
      }

      acc[key].count++;
      acc[key].tableData.push(personnel);

      return acc;
    }, {} as PersonnelReportTableDataRow);

    return { ...initialRanges, ...results };
  }, [data]);
  const ageReportData = Object.entries(ageReportTableData).map(
    ([key, value]) => ({
      category: key,
      [t('laNumberOfEmp')]: value.count,
    }),
  );
  const { ageTotal, ageCount } = [...data].reduce(
    (acc, personnel) => {
      const age = moment().diff(moment(new Date(personnel.fEmpDOB)), 'years');
      if (isNaN(age)) return acc;
      return { ageTotal: acc.ageTotal + age, ageCount: acc.ageCount + 1 };
    },
    { ageTotal: 0, ageCount: 0 },
  );

  const jobGradeEnums = getEnums(EnumListKey.JOB_GRADE);
  const jobGradeData = data.reduce((acc, personnel) => {
    const jobGrade =
      jobGradeEnums.find((jg) => jg.code === personnel.fEmpJobGrade)?.name ||
      '';

    if (!jobGrade) {
      return acc;
    }

    if (!acc[jobGrade]) {
      acc[jobGrade] = {
        count: 0,
        tableData: [],
      };
    }

    acc[jobGrade].count++;
    acc[jobGrade].tableData.push(personnel);

    return acc;
  }, {} as PersonnelReportTableDataRow);
  const jobGradeCategories = Object.keys(jobGradeData);
  const jobGradeCategoriesInit = jobGradeCategories.reduce(
    (acc, key) => ({ ...acc, [key]: 0 }),
    {} as { [key: string]: number },
  );
  const jobGradeReportData = Object.entries(jobGradeData).map(
    ([key, value]): BarDatum => ({
      jobGrade: key,
      ...jobGradeCategoriesInit,
      [t('laNumberOfEmp')]: value.count,
      [key]: value.count,
    }),
  );

  const jsonGenderData = data.reduce((acc, personnel) => {
    const gender = personnel.fEmpGender;

    let key = '';

    if (gender === 'M') key = t('laMale');
    else if (gender === 'F') key = t('laFemale');
    else if (gender === 'O') key = t('laOther');
    else key = t('laEmpty');

    if (!acc[key]) {
      acc[key] = {
        count: 0,
        tableData: [],
      };
    }

    acc[key].count++;
    acc[key].tableData.push(personnel);

    return acc;
  }, {} as PersonnelReportTableDataRow);
  const genderData = Object.entries(jsonGenderData)
    .map(([id, value]) => ({
      id,
      value: value.count,
    }))
    .filter((d) => d.value > 0);
  const genderDataCount = genderData.reduce((a, b) => a + b.value, 0);
  const genderLegend = Object.keys(jsonGenderData).map((id, idx) => ({
    id,
    label: id,
    color: analyticsColors[idx],
  }));

  const employmentTypeEnums = getEnums(EnumListKey.EMPLOYMENT_TYPE);
  const employmentTypeData = data.reduce((acc, personnel) => {
    const employmentType =
      employmentTypeEnums.find((jg) => jg.code === personnel.fEmpEmploymentType)
        ?.name || '';

    if (!acc[employmentType]) {
      acc[employmentType] = {
        count: 0,
        tableData: [],
      };
    }

    acc[employmentType].count++;
    acc[employmentType].tableData.push(personnel);

    return acc;
  }, {} as PersonnelReportTableDataRow);
  const employeeCount = Object.values(employmentTypeData).reduce(
    (sum, value) => sum + value.count,
    0,
  );
  const employmentTypeChartData = Object.entries(employmentTypeData)
    .map(([id, value]) => ({
      id,
      value: value.count,
    }))
    .filter((d) => d.value > 0);
  const employmentTypeLegend = employmentTypeEnums.map(({ name }, idx) => ({
    id: name,
    label: name,
    color: analyticsColors[idx],
  }));

  const employeeStatusEnums = getEnums(EnumListKey.EMPLOYEE_STATUS).filter(
    (enumItem) => ['ACTIVE', 'PASSIVE', 'ENDED'].includes(enumItem.code),
  );
  const employeeStatusData = useMemo(() => {
    return data.reduce((acc, personnel) => {
      const employeeStatus =
        employeeStatusEnums.find((es) => es.code === personnel.fEmpStatus)
          ?.name || '';

      if (!acc[employeeStatus]) {
        acc[employeeStatus] = {
          count: 0,
          tableData: [],
        };
      }

      acc[employeeStatus].count++;
      acc[employeeStatus].tableData.push(personnel);

      return acc;
    }, {} as PersonnelReportTableDataRow);
  }, [data, employeeStatusEnums]);
  const employeeStatusChartData = Object.entries(employeeStatusData)
    .map(([id, value]) => ({ id, value: value.count }))
    .filter((d) => d.value > 0);
  const employeeStatusCount = Object.values(employeeStatusData).reduce(
    (sum, value) => sum + value.count,
    0,
  );
  const employeeStatusLegend = employeeStatusEnums.map(({ name }, idx) => ({
    id: name,
    label: name,
    color: analyticsColors[idx],
  }));

  return {
    numberOfEmployeesReport: {
      reportData: numberOfEmployeesReportData,
      tableData: jsonCountryData,
    },
    ageReport: {
      ageReportData,
      ageReportTableData,
      averageAge: ageCount > 0 ? ageTotal / ageCount : 0,
    },
    jobGradeReport: {
      jobGradeCategories,
      jobGradeReportData,
      jobGradeReportTableData: jobGradeData,
    },
    genderReport: {
      genderDataCount,
      genderData,
      genderTableData: jsonGenderData,
      genderLegend,
    },
    employmentTypeReport: {
      employeeCount,
      employmentTypeChartData,
      employmentTypeTableData: employmentTypeData,
      employmentTypeLegend,
    },
    employeeStatusReport: {
      employeeStatusCount,
      employeeStatusChartData,
      employeeStatusTableData: employeeStatusData,
      employeeStatusLegend,
    },
  };
};
